import {
  Children,
  createContext,
  isValidElement,
  useContext,
  useMemo,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import { startOfMonth } from 'date-fns';
import { type DropdownProps } from 'react-day-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * `react-day-picker` only trims the month dropdown when `fromDate` and `toDate`
 * fall in the SAME year — ours never do (ten years back to today), so it hands
 * the caption all twelve months even on the current year, where the months
 * after this one lead nowhere. The calendar that owns the caption publishes its
 * bounds here so the dropdown can mark those options as unpickable.
 */
type CaptionBounds = { month: Date; min: Date; max: Date };

const CaptionBoundsContext = createContext<CaptionBounds | null>(null);

/** Wrap a `DayPicker` in this to bound its caption dropdown. */
export function DayPickerCaptionBounds({
  bounds,
  children,
}: {
  bounds: CaptionBounds;
  children: ReactNode;
}) {
  return <CaptionBoundsContext.Provider value={bounds}>{children}</CaptionBoundsContext.Provider>;
}

function isCaptionOptionDisabled(
  name: string | undefined,
  optionValue: string,
  bounds: CaptionBounds | null,
): boolean {
  if (!bounds) return false;
  const n = Number(optionValue);
  if (!Number.isFinite(n)) return false;
  const min = startOfMonth(bounds.min);
  const max = startOfMonth(bounds.max);
  if (name === 'months') {
    const candidate = new Date(bounds.month.getFullYear(), n, 1);
    return candidate < min || candidate > max;
  }
  if (name === 'years') {
    return n < min.getFullYear() || n > max.getFullYear();
  }
  return false;
}

function optionsFromSelectChildren(children: DropdownProps['children']) {
  const out: { value: string; label: string; disabled: boolean }[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement<{ value?: string | number; children?: ReactNode; disabled?: boolean }>(child))
      return;
    if (child.props.value === undefined || child.props.value === null) return;
    const value = String(child.props.value);
    const label =
      typeof child.props.children === 'string' || typeof child.props.children === 'number'
        ? String(child.props.children)
        : value;
    out.push({ value, label, disabled: Boolean(child.props.disabled) });
  });
  return out;
}

export function DayPickerCaptionDropdown({
  name,
  caption,
  children,
  className,
  style,
  value,
  onChange,
  'aria-label': ariaLabel,
}: DropdownProps) {
  const bounds = useContext(CaptionBoundsContext);
  const options = useMemo(() => optionsFromSelectChildren(children), [children]);
  const stringValue = value === undefined || value === null ? undefined : String(value);

  return (
    <div className={className} style={style}>
      <Select
        modal={false}
        value={stringValue}
        onValueChange={(next) => {
          onChange?.({
            target: { value: next, name: name ?? '' },
          } as ChangeEvent<HTMLSelectElement>);
        }}
      >
        <SelectTrigger
          aria-label={ariaLabel}
          className="h-8 w-full gap-1 rounded-lg border-[#E4E7EC] px-2.5 text-[12.5px] font-semibold text-[#101828] [&>span]:line-clamp-1 [&>svg]:h-3 [&>svg]:w-3"
        >
          <SelectValue>{caption}</SelectValue>
        </SelectTrigger>
        <SelectContent
          data-date-range-caption-menu=""
          position="popper"
          className="z-[90]"
        >
          {options.map((opt) => (
            <SelectItem
              key={opt.value}
              value={opt.value}
              disabled={opt.disabled || isCaptionOptionDisabled(name, opt.value, bounds)}
              className="data-[disabled]:pointer-events-auto data-[disabled]:cursor-not-allowed"
            >
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
